import { WeeklyDinnerItem as WeeklyDinnerItemType } from "@mjolnerbacken/shared/types/weekly-dinner-item";
import { useEffect, useRef, useState } from "react";

import { useWeeklyDinnerItems } from "../hooks/weekly-dinner-items";
import { updateWeeklyDinnerItem } from "../services/weekly-dinner";
import { useLocale } from "../stores/locale";
import { Input } from "./input";

export function WeeklyDinnerItem({ item }: { item: WeeklyDinnerItemType }) {
  const t = useLocale((state) => state.locale);
  const { refetch: refetchWeeklyDinner } = useWeeklyDinnerItems();

  const [inputValue, setInputValue] = useState(item.value);
  const [updatedAt, setUpdatedAt] = useState(item.updatedAt);
  const [editMode, setEditMode] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const today = new Date();
  // Set to begining of day
  today.setHours(0, 0, 0, 0);
  const nextDay = getNextWeekdayDate(mapWeekDayToName(item.weekday), updatedAt);
  const dateHasPassed = today > nextDay;

  const updateValue = async () => {
    setEditMode(false);

    if (item.value.trim() === inputValue.trim()) {
      return;
    }

    setUpdatedAt(new Date());
    await updateWeeklyDinnerItem(item.id, {
      value: inputValue,
    });
    await refetchWeeklyDinner();
  };

  useEffect(() => {
    setInputValue(item.value);
  }, [item.value]);

  return (
    <li className="flex gap-4 px-3 py-2 bg-gray-50 shadow-sm">
      <span
        className={`font-bold w-16 text-right ${dateHasPassed ? "line-through" : ""}`}
      >
        {t.weekdays[mapWeekDayToName(item.weekday)]}:
      </span>
      <div className="grow flex items-start min-h-[50px]">
        {editMode ? (
          <Input
            ref={inputRef}
            className="grow !m-0"
            inputClassName="!w-full relative left-[-1px]"
            size="mini"
            type="textarea"
            value={inputValue}
            onBlur={() => {
              (async () => {
                await updateValue();
              })();
            }}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.shiftKey) {
                updateValue();
              }
            }}
          />
        ) : (
          <button
            className={`text-left flex grow min-h-[50px] items-start text-gray-800 ${dateHasPassed ? "line-through" : ""}`}
            onClick={() => {
              setEditMode(true);
              setTimeout(() => {
                inputRef.current?.focus();
              }, 0);
            }}
          >
            <span className="whitespace-pre-line">{inputValue}</span>
          </button>
        )}
      </div>
    </li>
  );
}

function getNextWeekdayDate(weekday: Weekday, afterDate: Date): Date {
  const dayNumbers: Record<Weekday, number> = {
    monday: 0,
    tuesday: 1,
    wednesday: 2,
    thursday: 3,
    friday: 4,
    saturday: 5,
    sunday: 6,
  };

  const targetDay = dayNumbers[weekday];

  const result = new Date(afterDate);

  // Get the "monday-based" day of week
  const currentDay = (result.getDay() + 6) % 7; // Adjust so Monday is 0
  let daysToAdd = (targetDay - currentDay + 7) % 7;
  if (daysToAdd === 0) {
    daysToAdd = 7; // Always move to next week if same day
  }

  result.setDate(result.getDate() + daysToAdd);
  // Set to begining of day
  result.setHours(0, 0, 0, 0);
  return result;
}

type Weekday =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

function mapWeekDayToName(weekDay: number): Weekday {
  return [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ][weekDay] as Weekday;
}
